import React, { useState, useEffect, useCallback } from 'react';

// ============================================
// Recruit & Earn — referrer dashboard (route: /referrals)
// Shows referred users (masked), milestone progress, and claim buttons.
// ============================================

const fmt = (n) => '$' + Math.round(n).toLocaleString();

const STATUS_CHIP = {
  locked:   { label: 'Locked',        color: '#71717a', bg: 'rgba(255,255,255,0.05)',  border: 'rgba(255,255,255,0.1)' },
  eligible: { label: 'Claim',         color: '#1c1205', bg: 'linear-gradient(135deg, #fbbf24, #f59e0b)', border: 'transparent' },
  claimed:  { label: 'In Review',     color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',   border: 'rgba(251,191,36,0.35)' },
  approved: { label: 'Paid',          color: '#22c55e', bg: 'rgba(34,197,94,0.12)',    border: 'rgba(34,197,94,0.35)' },
  denied:   { label: 'Denied',        color: '#f87171', bg: 'rgba(239,68,68,0.12)',    border: 'rgba(239,68,68,0.35)' }
};

export default function ReferralsPage({ apiUrl, getToken, user, loginWithTwitch }) {
  const [data, setData] = useState(null);
  const [refInfo, setRefInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [refInput, setRefInput] = useState('');
  const [settingRef, setSettingRef] = useState(false);
  const [message, setMessage] = useState(null);
  const [pageTab, setPageTab] = useState('dashboard'); // dashboard | leaderboard
  const [lb, setLb] = useState(null); // top recruiters (public, lazy-loaded)

  useEffect(() => {
    if (pageTab !== 'leaderboard' || lb) return;
    fetch(`${apiUrl}/api/referrals/leaderboard`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setLb(d.leaderboard || []); })
      .catch(() => {});
  }, [pageTab, lb, apiUrl]);

  const load = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const [dashRes, refRes] = await Promise.all([
        fetch(`${apiUrl}/api/referrals/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${apiUrl}/api/referrals/my-referrer`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (dashRes.ok) setData(await dashRes.json());
      if (refRes.ok) setRefInfo(await refRes.json());
    } catch (e) {
      console.error('Referrals load error:', e);
    }
    setLoading(false);
  }, [apiUrl, getToken]);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const claimReward = async (rewardId) => {
    setClaimingId(rewardId);
    try {
      const res = await fetch(`${apiUrl}/api/referrals/rewards/${rewardId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ type: 'success', text: 'Claim submitted! An admin will review it shortly.' });
        load();
      } else {
        setMessage({ type: 'error', text: body.error || 'Failed to claim reward' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to claim reward' });
    }
    setClaimingId(null);
  };

  const submitReferrer = async () => {
    if (!refInput.trim()) return;
    setSettingRef(true);
    try {
      const res = await fetch(`${apiUrl}/api/referrals/set-referrer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ username: refInput.trim() })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ type: 'success', text: `Referrer set to ${body.referrer?.displayName || refInput.trim()}!` });
        setRefInput('');
        load();
      } else {
        setMessage({ type: 'error', text: body.error || 'Failed to set referrer' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to set referrer' });
    }
    setSettingRef(false);
  };

  const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.5rem' };
  const label = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: '#71717a' };

  const milestones = data?.milestones || [];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 className="page-title">Recruit &amp; Earn</h1>
        <p className="page-subtitle">
          Refer friends to guild <strong>SNUTZY</strong> — as they wager, you unlock cash rewards up to <strong>$500 per recruit</strong>.
        </p>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.6rem' }}>
        {[['dashboard', '📊 My Referrals'], ['leaderboard', '🏆 Top Recruiters']].map(([key, lbl]) => (
          <button key={key} onClick={() => setPageTab(key)} style={{
            padding: '0.55rem 1.4rem', borderRadius: '10px', cursor: 'pointer', fontWeight: 800, fontSize: '0.88rem', fontFamily: 'Outfit, sans-serif',
            background: pageTab === key ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${pageTab === key ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
            color: pageTab === key ? '#fff' : '#a1a1aa',
            boxShadow: pageTab === key ? '0 4px 16px rgba(168,85,247,0.3)' : 'none'
          }}>{lbl}</button>
        ))}
      </div>

      {pageTab === 'leaderboard' ? (
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          {!lb ? (
            <p style={{ textAlign: 'center', color: '#71717a' }}>Loading the leaderboard…</p>
          ) : lb.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#71717a', padding: '2rem 0' }}>No recruiters on the board yet — be the first! 🏆</p>
          ) : (
            <>
              {/* Podium — ranked by recruits' combined wager */}
              {lb.length >= 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '0.7rem', marginBottom: '1.2rem' }}>
                  {[lb[1], lb[0], lb[2]].map((p, i) => {
                    if (!p) return <div key={i} style={{ width: '150px' }} />;
                    const first = p.rank === 1;
                    return (
                      <div key={p.rank} style={{
                        width: first ? '190px' : '150px', textAlign: 'center', padding: first ? '1.3rem 0.8rem' : '1rem 0.7rem',
                        background: first ? 'linear-gradient(180deg, rgba(251,191,36,0.12), rgba(251,191,36,0.03))' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${first ? 'rgba(251,191,36,0.5)' : p.rank === 2 ? 'rgba(212,212,216,0.3)' : 'rgba(217,119,6,0.35)'}`,
                        borderRadius: '16px', boxShadow: first ? '0 8px 30px rgba(251,191,36,0.12)' : 'none'
                      }}>
                        <div style={{ fontSize: first ? '1.8rem' : '1.3rem' }}>{['🥇', '🥈', '🥉'][p.rank - 1]}</div>
                        <div style={{ fontWeight: 900, fontSize: first ? '1rem' : '0.85rem', color: first ? '#fbbf24' : '#fff', margin: '0.25rem 0 0.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        <div style={{ fontWeight: 900, fontSize: first ? '1.15rem' : '0.95rem', color: '#22c55e' }}>{fmt(p.totalWagered)}</div>
                        <div style={{ fontSize: '0.62rem', color: '#71717a', fontWeight: 700, letterSpacing: '1px' }}>RECRUITS WAGERED</div>
                        <div style={{ marginTop: '0.3rem', display: 'inline-block', fontSize: '0.64rem', fontWeight: 700, padding: '0.14rem 0.5rem', borderRadius: '999px', background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.3)', color: '#c4b5fd' }}>
                          {p.recruits} recruit{p.recruits !== 1 ? 's' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Rest of the board */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {lb.slice(3).map(p => (
                  <div key={p.rank} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.6rem 1rem', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
                    <span style={{ fontWeight: 900, color: '#71717a', minWidth: '2rem', textAlign: 'center', fontSize: '0.85rem' }}>#{p.rank}</span>
                    <span style={{ fontWeight: 800, color: '#fff', flex: 1, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    <span style={{ fontSize: '0.68rem', color: '#c4b5fd', fontWeight: 700 }}>{p.recruits} recruit{p.recruits !== 1 ? 's' : ''}</span>
                    <span style={{ fontWeight: 900, color: '#22c55e', fontSize: '0.92rem', minWidth: '90px', textAlign: 'right' }}>{fmt(p.totalWagered)}</span>
                  </div>
                ))}
              </div>
              <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#71717a', marginTop: '1rem' }}>
                Ranked by your recruits' weighted wager under guild SNUTZY — quality over quantity.
              </p>
            </>
          )}
        </div>
      ) : !user ? (
        <div style={{ ...card, textAlign: 'center', maxWidth: '440px', margin: '0 auto' }}>
          <p style={{ color: '#a1a1aa', marginBottom: '1.2rem' }}>Login to see your referrals and start earning.</p>
          <button className="twitch-login-btn" onClick={loginWithTwitch} style={{ margin: '0 auto' }}>Login with Twitch</button>
        </div>
      ) : loading ? (
        <p style={{ textAlign: 'center', color: '#71717a' }}>Loading your referrals…</p>
      ) : (
        <>
          {message && (
            <div style={{
              padding: '0.8rem 1.2rem', borderRadius: '10px', marginBottom: '1.2rem', fontSize: '0.9rem', fontWeight: 600,
              background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
              color: message.type === 'success' ? '#4ade80' : '#fca5a5',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem'
            }}>
              {message.text}
              <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem' }}>×</button>
            </div>
          )}

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ ...card, textAlign: 'center', padding: '1.2rem' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#c084fc' }}>{(data?.totals.siteWide ?? 0).toLocaleString()}</div>
              <div style={label}>Total Recruits</div>
            </div>
            <div style={{ ...card, textAlign: 'center', padding: '1.2rem' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff' }}>{data?.totals.referred ?? 0}</div>
              <div style={label}>Your Recruits</div>
            </div>
            <div style={{ ...card, textAlign: 'center', padding: '1.2rem' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#22c55e' }}>{fmt(data?.totals.earnedApproved ?? 0)}</div>
              <div style={label}>Earned</div>
            </div>
            <div style={{ ...card, textAlign: 'center', padding: '1.2rem' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fbbf24' }}>{fmt(data?.totals.pendingReview ?? 0)}</div>
              <div style={label}>In Review</div>
            </div>
          </div>

          {/* How it works + milestones */}
          <div style={{ ...card, marginBottom: '1.5rem' }}>
            <div style={{ ...label, color: '#c084fc', marginBottom: '0.8rem' }}>[ How it works ]</div>
            <p style={{ color: '#a1a1aa', fontSize: '0.92rem', lineHeight: 1.65, margin: '0 0 1rem' }}>
              Tell your friends to enter <strong style={{ color: '#fff' }}>your Twitch or Shuffle username</strong> when
              they join snutzy.gg (they can set it within 48 hours of signing up). As they wager under guild SNUTZY,
              you unlock these milestones per recruit (based on <strong style={{ color: '#fff' }}>weighted</strong> wager, same as the leaderboard):
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
              {milestones.map(m => (
                <span key={m.wager} style={{ padding: '0.4rem 0.85rem', borderRadius: '999px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', fontSize: '0.8rem', fontWeight: 700, color: '#d8b4fe' }}>
                  {fmt(m.wager)} weighted → <span style={{ color: '#fbbf24' }}>{fmt(m.reward)}</span>
                </span>
              ))}
            </div>
            <p style={{ color: '#f87171', fontSize: '0.8rem', marginTop: '1rem', marginBottom: 0 }}>
              ⚠️ All claims are manually reviewed. Any artificial abuse of the referral system (self-referrals, alt
              accounts, referral rings) will disqualify you from all rewards.
            </p>
          </div>

          {/* Your referrer / set referrer */}
          <div style={{ ...card, marginBottom: '1.5rem' }}>
            <div style={{ ...label, marginBottom: '0.7rem' }}>Your referrer</div>
            {refInfo?.referrer ? (
              <p style={{ margin: 0, color: '#e4e4e7', fontSize: '0.95rem' }}>
                🤝 You were referred by <strong style={{ color: '#c084fc' }}>{refInfo.referrer.displayName}</strong>
              </p>
            ) : refInfo?.canSet ? (
              <div>
                <p style={{ color: '#a1a1aa', fontSize: '0.88rem', margin: '0 0 0.8rem' }}>
                  Did someone refer you? Enter their Twitch or Shuffle username (available for 48 hours after signup):
                </p>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <input
                    value={refInput}
                    onChange={e => setRefInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitReferrer(); }}
                    placeholder="Their username"
                    style={{ flex: 1, minWidth: '200px', padding: '0.6rem 0.9rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem', fontFamily: 'Outfit, sans-serif' }}
                  />
                  <button
                    onClick={submitReferrer}
                    disabled={settingRef || !refInput.trim()}
                    style={{ padding: '0.6rem 1.4rem', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: settingRef || !refInput.trim() ? 0.6 : 1 }}
                  >
                    {settingRef ? 'Saving…' : 'Set Referrer'}
                  </button>
                </div>
              </div>
            ) : (
              <p style={{ margin: 0, color: '#71717a', fontSize: '0.88rem' }}>
                No referrer set — the 48-hour window after signup has passed. Forgot to enter one? Open a ticket
                in Discord and an admin can still link it for you.
              </p>
            )}
          </div>

          {/* Referred users */}
          <div style={{ ...card }}>
            <div style={{ ...label, marginBottom: '1rem' }}>Your recruits ({data?.referrals.length ?? 0})</div>
            {(!data || data.referrals.length === 0) ? (
              <p style={{ color: '#71717a', fontSize: '0.9rem', margin: 0 }}>
                No recruits yet — share your username and have friends enter it when they sign up!
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {data.referrals.map(ref => {
                  const nextMilestone = milestones.find(m => ref.wager < m.wager);
                  const progress = nextMilestone ? Math.min(100, (ref.wager / nextMilestone.wager) * 100) : 100;
                  const rewardByMilestone = {};
                  for (const rw of ref.rewards) rewardByMilestone[rw.milestone_amount] = rw;
                  return (
                    <div key={ref.id} style={{ padding: '1.1rem 1.2rem', borderRadius: '12px', background: 'rgba(255,255,255,0.025)', border: `1px solid ${ref.status === 'flagged' ? 'rgba(239,68,68,0.35)' : 'rgba(255,255,255,0.07)'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.7rem' }}>
                        <div style={{ fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          {ref.username}
                          {ref.status === 'flagged' && <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '999px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}>UNDER REVIEW</span>}
                          {!ref.shuffleLinked && <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', color: '#a1a1aa' }} title="Wager tracking starts once they link their Shuffle account on snutzy.gg">NO SHUFFLE LINKED</span>}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#a1a1aa' }}>
                          Weighted wager: <strong style={{ color: '#fff' }}>{fmt(ref.wager)}</strong>
                          {nextMilestone && <span> · next: {fmt(nextMilestone.wager)}</span>}
                        </div>
                      </div>
                      {/* Progress to next milestone */}
                      <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden', marginBottom: '0.85rem' }}>
                        <div style={{ height: '100%', width: `${progress}%`, borderRadius: '3px', background: 'linear-gradient(90deg, #a855f7, #fbbf24)', transition: 'width 0.6s ease' }} />
                      </div>
                      {/* Milestone chips */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {milestones.map(m => {
                          const rw = rewardByMilestone[m.wager];
                          const state = rw ? rw.status : 'locked';
                          const chip = STATUS_CHIP[state] || STATUS_CHIP.locked;
                          const clickable = state === 'eligible' && ref.status !== 'flagged';
                          return (
                            <button
                              key={m.wager}
                              onClick={() => clickable && claimReward(rw.id)}
                              disabled={!clickable || claimingId === rw?.id}
                              title={rw?.admin_response ? `Admin: ${rw.admin_response}` : `${fmt(m.wager)} weighted wager milestone`}
                              style={{
                                padding: '0.35rem 0.75rem', borderRadius: '999px',
                                fontSize: '0.74rem', fontWeight: 700, fontFamily: 'Outfit, sans-serif',
                                background: chip.bg, color: chip.color,
                                border: `1px solid ${chip.border}`,
                                cursor: clickable ? 'pointer' : 'default',
                                opacity: claimingId === rw?.id ? 0.6 : 1
                              }}
                            >
                              {fmt(m.reward)} — {state === 'eligible' ? 'Claim now' : chip.label}
                            </button>
                          );
                        })}
                      </div>
                      {/* Show denial reasons */}
                      {ref.rewards.filter(rw => rw.status === 'denied' && rw.admin_response).map(rw => (
                        <p key={rw.id} style={{ color: '#fca5a5', fontSize: '0.78rem', margin: '0.6rem 0 0' }}>
                          ❌ {fmt(rw.reward_amount)} claim denied: {rw.admin_response}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
